import React, { useMemo, useState } from 'react';

import { link } from '@aline/routes';
import type { Reference, StoreObject } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Autocomplete, IconButton, Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { DataGridPremium, useGridApiContext, useGridApiRef } from '@mui/x-data-grid-premium';
import { AlineInput } from 'src/AlineInput';
import {
  AlineAutocompleteInput,
  AlineCheckbox,
  AlineTooltip,
  ConfirmationDialog,
  FiltersToolbarButton,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  MergeDialog,
  NewPartyButton,
  PartiesAutocomplete,
  PartyNameCell,
  ReportFiltersPanel,
  SearchInput,
  SmallMergeIconButton,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { GroupIcon } from 'src/components/icons/GroupIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { getEstimatedReportRowHeight } from 'src/components/reports/Report';
import type { Apollo } from 'src/gql/generated';
import { getIsBrowserHandledLinkClick, openLink, overflowMenuCSSId } from 'src/helpers';
import {
  createDataGridPremiumPaperStyles,
  FiltersAnchorProvider,
  useBulkDeleteParties,
  useDataGridSearch,
  useFiltersAnchorState,
  useGetSelectedRowIds,
  useMergeParties,
  usePartyDataSource,
} from 'src/hooks';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  deleteWrapper: {
    padding: theme.spacing(2, 0),
  },
  emptyIcon: {
    height: 32,
    width: 32,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  title: {
    flexShrink: 0,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  toolbar: {
    flexShrink: 0,
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover #${overflowMenuCSSId}`]: {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

type PartyRow = Pick<Apollo.Party, 'description' | 'documentCount' | 'id' | 'name' | '__typename'>;
type PartyMergeEntity = Pick<Apollo.Party, 'id' | 'name' | '__typename'>;

const SELECTED_PARTY_FRAGMENT = gql`
  fragment SelectedParty on Party {
    __typename
    id
    name
  }
`;

const confirmDeleteString = 'DELETE';

export function PartyList(): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiRef();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const {
    dataSource,
    decrementTotalCount,
    filterModelTuple: [filterModel, setFilterModel],
    onDataSourceError,
    sortModelTuple: [sortModel, setSortModel],
    totalCount,
  } = usePartyDataSource({
    apiRef,
    initialSortModel: [{ field: 'name' satisfies keyof PartyRow, sort: 'asc' }],
  });
  const columnDefs = useMemo(
    () => [
      {
        field: 'name',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        filterable: true,
        flex: 1.5,
        headerName: 'Legal Name',
        renderCell: PartyNameCell,
        type: 'string' as const,
      },
      {
        field: 'description',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        filterable: true,
        flex: 1,
        headerName: 'Description',
        renderCell: PartyDescriptionCell,
        type: 'string' as const,
      },
      {
        align: 'left' as const,
        field: 'documentCount',
        flex: 1,
        headerAlign: 'left' as const,
        headerName: '# of Documents',
        type: 'number' as const,
      },
    ],
    []
  );

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={classes.container}>
        <div className={classes.listContainer}>
          <DataGridPremium<PartyRow>
            checkboxSelection
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            keepNonExistentRowsSelected
            lazyLoading
            pagination
            showToolbar
            apiRef={apiRef}
            className={classes.xGrid}
            columns={columnDefs}
            dataSource={dataSource}
            filterModel={filterModel}
            getEstimatedRowHeight={getEstimatedReportRowHeight}
            getRowHeight={getAutoRowHeight}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            slotProps={{
              filterPanel: {
                filterableColumns: 'visible',
              },
              footer: {
                noun: 'parties',
                totalCount,
              },
              loadingOverlay: {
                noRowsVariant: 'linear-progress',
                variant: 'linear-progress',
              },
              panel: {
                placement: 'bottom-start',
                target: filtersAnchorEl ?? undefined,
              },
              toolbar: {
                decrementTotalCount,
                totalCount,
              },
            }}
            slots={{
              baseCheckbox: AlineCheckbox,
              baseTooltip: GridAlineTooltip,
              filterPanel: ReportFiltersPanel,
              footer: ListCountFooter,
              noRowsOverlay: PartiesListEmpty,
              toolbar: PartiesListToolbar,
            }}
            sortModel={sortModel}
            sx={createDataGridPremiumPaperStyles}
            onDataSourceError={onDataSourceError}
            onFilterModelChange={setFilterModel}
            onRowClick={({ id, row }, event) => {
              /**
               * A modifier or middle click is a request for a new browser tab. The row background is
               * not a link, so honouring it here would navigate the current tab instead — do nothing
               * and let the row's name link be the thing that opens in a new tab.
               */
              if (getIsBrowserHandledLinkClick(event)) return;

              openLink(
                link.PartyDetailDocuments({ partyId: String(id) }),
                row.name || 'Untitled Party'
              );
            }}
            onSortModelChange={setSortModel}
          />
        </div>
      </div>
    </FiltersAnchorProvider>
  );
}

function PartiesListEmpty(): JSX.Element {
  const { classes } = useStyles();

  return (
    <ListEmpty
      icon={<GroupIcon className={classes.emptyIcon} fill="#9CA7AB" />}
      subtitle="Click new to create a party"
      title="No parties"
    />
  );
}

function PartiesListToolbar({
  decrementTotalCount,
  totalCount = 0,
}: {
  decrementTotalCount?: (by: number) => void;
  totalCount?: number;
}): JSX.Element {
  const { classes: gridStyles, cx } = useStyles();
  const apiRef = useGridApiContext();
  const client = useApolloClient();
  const { classes } = useListToolbarStyles();
  const { mergeParties } = useMergeParties();
  const { search, setSearch } = useDataGridSearch();
  const { bulkDeleteParties } = useBulkDeleteParties();
  const [deleteText, setDeleteText] = useState<string>('');
  const [deletePartyIds, setDeletePartyIds] = useState<UUID[]>([]);
  const { selectedRowIds: selectedPartyIds } = useGetSelectedRowIds();
  const [mergeDialogOpen, setMergeDialogOpen] = useState<boolean>(false);
  /**
   * The full party objects for the selected ids, resolved from the normalized
   * Apollo cache (with the loaded grid row as a fallback) so the merge dialog
   * never re-fetches the whole party list and selections that scrolled out of
   * view aren't dropped from the "will be deleted" list.
   */
  const selectedParties = useMemo<PartyMergeEntity[]>(
    () =>
      selectedPartyIds.flatMap(id => {
        /**
         * Existence check, not truthiness — a party with an empty name is still
         * a real selection and must not be dropped from the dialog.
         */
        const cached = client.cache.readFragment<PartyMergeEntity>({
          fragment: SELECTED_PARTY_FRAGMENT,
          id: client.cache.identify({ __typename: 'Party', id }),
        });
        if (cached) return [cached];

        const row = apiRef.current?.getRow(id);
        return typeof row?.name === 'string'
          ? [{ __typename: 'Party' as const, id, name: row.name }]
          : [];
      }),
    [apiRef, client, selectedPartyIds]
  );
  const hasSelectedParties = Boolean(selectedPartyIds.length);
  /**
   * Merge needs at least two parties to combine, so require both a selection
   * and more than one party in the (filtered) result set.
   */
  const showMergeButton = totalCount >= 2 && selectedPartyIds.length >= 1;

  function clearDeletePartiesState() {
    setDeleteText('');
    setDeletePartyIds([]);
  }

  /**
   * Remove rows from the grid in place after a mutation — no full refetch. The
   * cursor source of truth for this grid is the Apollo `partyRows.rows` array
   * (safeGetRows slices the page after the cursor row from it), so we prune it
   * for every cached filter/sort variant, then mirror the removal into MUI's
   * visible row store and selection. Clearing the dataSource cache is a cheap
   * guard against a stale chunk being served on a fast re-scroll.
   */
  function removePartiesFromGrid(removedIds: readonly UUID[]) {
    if (!removedIds.length) return;
    const removedSet = new Set(removedIds);

    client.cache.modify({
      fields: {
        partyRows(existing, { readField }) {
          const rows = readField<readonly Reference[]>('rows', existing) ?? [];
          const nextRows = rows.filter(ref => {
            const id = readField<UUID>('id', ref);
            return !id || !removedSet.has(id);
          });
          if (nextRows.length === rows.length) return existing;

          const meta = readField<StoreObject>('meta', existing);
          const prevTotal = readField<number>('totalCount', meta) ?? 0;
          return {
            ...existing,
            meta: { ...meta, totalCount: Math.max(0, prevTotal - (rows.length - nextRows.length)) },
            rows: nextRows,
          };
        },
      },
    });

    apiRef.current?.updateRows(removedIds.map(id => ({ _action: 'delete', id })));
    apiRef.current?.dataSource?.cache?.clear();
    apiRef.current?.setRowSelectionModel({ ids: new Set(), type: 'include' });
    decrementTotalCount?.(removedIds.length);
  }

  async function handleMergeParties(targetPartyId: UUID, sourcePartyIds: UUID[]) {
    const result = await mergeParties({ variables: { input: { sourcePartyIds, targetPartyId } } });
    /**
     * errorPolicy doesn't throw on failure, so check for data + no errors. On
     * success the merged-away sources leave the grid; the target's documentCount
     * syncs into its loaded row via the partyRows cache-only watchQuery (the
     * MergeParties mutation returns the target's new count).
     */
    const succeeded = Boolean(result.data?.mergeParties) && !result.errors?.length;
    if (succeeded) removePartiesFromGrid(sourcePartyIds);
    // Signal failure to MergeDialog (falsy data) so it doesn't show a success snack.
    return succeeded ? result : { data: undefined };
  }

  return (
    <>
      <div className={cx(classes.toolbar, gridStyles.toolbar)}>
        <Typography className={gridStyles.title} variant="h6">
          Parties
        </Typography>
        <FiltersToolbarButton />
        <Spacer />
        {showMergeButton && (
          <AlineTooltip arrow placement="top" title="Merge parties">
            <div>
              <SmallMergeIconButton onClick={() => setMergeDialogOpen(!mergeDialogOpen)} />
            </div>
          </AlineTooltip>
        )}
        <AlineTooltip arrow placement="top" title="Delete parties">
          <div>
            <IconButton
              size="small"
              style={{ visibility: hasSelectedParties ? 'visible' : 'hidden' }}
              onClick={() => setDeletePartyIds(selectedPartyIds)}
            >
              <TrashcanIcon />
            </IconButton>
          </div>
        </AlineTooltip>
        <SearchInput placeholder="Search parties..." search={search} setSearch={setSearch} />
        <NewPartyButton />
      </div>
      <MergeDialog
        entities={selectedParties}
        getDisabledReason={targetId => {
          return !targetId ? 'Select a target party' : '';
        }}
        handleMerge={handleMergeParties}
        message={`Are you sure you want to merge? All selected parties will be deleted and merged into the target party. This action cannot be undone.`}
        open={mergeDialogOpen}
        renderTargetPicker={({ availableTargets, onChange, value }) =>
          /**
           * One source → merge into any other party (paginated search, excluding
           * the selected source). Multiple sources → pick the survivor among the
           * selected ones. Branch on the reliable selected-id count, not the
           * loaded-rows list which can be partial under lazy loading.
           */
          selectedPartyIds.length <= 1 ? (
            <PartiesAutocomplete
              filterPred={party => !selectedPartyIds.includes(party.id)}
              placeholder="Select a merge target..."
              value={value}
              onChange={(_event, newValue) =>
                onChange(newValue?.__typename === 'Party' ? newValue : null)
              }
            />
          ) : (
            <Autocomplete<PartyMergeEntity>
              fullWidth
              getOptionLabel={party => party.name}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              options={availableTargets}
              renderInput={params => (
                <AlineAutocompleteInput {...params} placeholder="Select a merge target..." />
              )}
              value={value}
              onChange={(_event, newValue) => onChange(newValue)}
            />
          )
        }
        selectedIds={selectedPartyIds}
        title="Merge parties?"
        onClose={() => setMergeDialogOpen(false)}
      />
      <ConfirmationDialog
        message="Are you sure you want to delete the selected parties? This action cannot be undone."
        open={Boolean(deletePartyIds.length)}
        primaryDisabledReason={
          deleteText !== confirmDeleteString ? `Type ${confirmDeleteString} to proceed` : ''
        }
        primaryText="Delete"
        title="Delete parties"
        onClickPrimary={async () => {
          if (!deletePartyIds.length) return;
          const result = await bulkDeleteParties(deletePartyIds);
          /**
           * Remove exactly the ids the server reports deleted (some may be
           * unauthorized), and only if the mutation didn't error.
           */
          const deletedIds = result.data?.bulkDeleteParties ?? [];
          if (deletedIds.length && !result.error) removePartiesFromGrid(deletedIds);
        }}
        onClose={clearDeletePartiesState}
        onExit={clearDeletePartiesState}
      >
        <div className={gridStyles.deleteWrapper}>
          <AlineInput
            autoFocus
            data-cy="confirm-delete-input"
            placeholder={`Type ${confirmDeleteString} to confirm...`}
            value={deleteText}
            onChange={e => setDeleteText(e.target.value)}
          />
        </div>
      </ConfirmationDialog>
    </>
  );
}

const useDescriptionStyles = makeStyles()(() => ({
  description: {
    ...createClampStyles(3),
    width: '100%',
  },
}));

function PartyDescriptionCell({ value }: GridCellParams<PartyRow, string>): JSX.Element {
  const { classes } = useDescriptionStyles();

  return <Typography className={classes.description}>{value || 'No description'}</Typography>;
}

// A module-level function keeps its identity, so the grid does not re-measure every row on render.
function getAutoRowHeight(): 'auto' {
  return 'auto';
}
